import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button } from 'react-bootstrap';
import { IconArrowLeft, IconEdit, IconPlus, IconTrash } from '@tabler/icons-react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../../shared/api';
import { useRealtimeEvents } from '../../shared/useRealtimeEvents';
import { useConfirm } from '../../shared/ConfirmContext';
import { useCurrency } from '../../shared/CurrencyContext';
import { usePermissions } from '../../shared/permissions';
import { CenteredLoader } from '../../components/LoadingSpinner';
import PageSection from '../../components/layout/PageSection';
import PageShell from '../../components/layout/PageShell';
import TariffListTable from './components/TariffListTable';
import TariffFormModal from './components/TariffFormModal';
import ProductInfoCard from './components/ProductInfoCard';
import ProductStockTable from './components/ProductStockTable';
import ProductStockEditModal from './components/ProductStockEditModal';
import { useDefaultDimensionUnit } from './hooks/useDefaultDimensionUnit';
import { buildProductDimensionsPayload, productDimensionsForm } from './productDimensions';

const asList = (response: any) => response?.results || response || [];

const emptyTariffForm = {
  name: '', tariff_type: null, amount: '', amount_vat: '',
  valid_from: '', valid_to: '', priority: 0, is_active: true, is_promotional: false, note: '', description: '',
};

const selectTheme = (theme: any) => ({
  ...theme,
  colors: {
    ...theme.colors,
    primary: 'var(--tblr-primary)',
    primary75: 'var(--tblr-primary-rgb)',
    primary50: 'var(--tblr-primary-rgb)',
    primary25: 'var(--tblr-primary-rgb)',
    danger: 'var(--tblr-danger)',
    dangerLight: 'var(--tblr-danger-rgb)',
    neutral0: 'var(--tblr-bg-forms)',
    neutral5: 'var(--tblr-bg-surface)',
    neutral10: 'var(--tblr-bg-surface)',
    neutral20: 'var(--tblr-border-color)',
    neutral30: 'var(--tblr-border-color)',
    neutral40: 'var(--tblr-body-color-secondary)',
    neutral50: 'var(--tblr-body-color-secondary)',
    neutral60: 'var(--tblr-body-color-secondary)',
    neutral70: 'var(--tblr-body-color-secondary)',
    neutral80: 'var(--tblr-body-color)',
    neutral90: 'var(--tblr-body-color)',
  },
});

export default function ProductDetail() {
  const { productId } = useParams();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const { formatCurrency } = useCurrency();
  const { canManage, hasPerm } = usePermissions();

  const [editing, setEditing] = useState(() => new URLSearchParams(location.search).get('edit') === '1');
  const [form, setForm] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  useDefaultDimensionUnit(form, setForm);

  const [showTariffModal, setShowTariffModal] = useState(false);
  const [editingTariff, setEditingTariff] = useState<any>(null);
  const [tariffForm, setTariffForm] = useState(emptyTariffForm);
  const [tariffError, setTariffError] = useState<string | null>(null);

  const [showStockModal, setShowStockModal] = useState(false);
  const [editingStockItem, setEditingStockItem] = useState<any>(null);
  const [stockForm, setStockForm] = useState({ quantity_owned: '0', quantity_available: '0' });
  const [stockError, setStockError] = useState<string | null>(null);

  // 1. Načtení detailu produktu (s bezpečným unwrappingem res.data)
  const { data: product, isLoading, isError } = useQuery({
    queryKey: ['product', productId],
    queryFn: async () => {
      const res: any = await api.get(`warehouses/api/products/${productId}/`);
      return res?.data ?? res;
    },
  });

  // 2. Načtení výchozí DPH pro přepočty
  const { data: defaultTax } = useQuery({
    queryKey: ['defaultTax'],
    queryFn: async () => {
      const res: any = await api.get('settings/api/tax/default/');
      return res?.data ?? res;
    },
  });

  const { data: stockItems = [] } = useQuery({
    queryKey: ['product-stock-items', productId],
    queryFn: () => api.get(`warehouses/api/stock-items/?product=${productId}`).then(asList),
  });

  const { data: tariffs = [] } = useQuery({
    queryKey: ['product-tariffs', productId],
    queryFn: () => api.get(`warehouses/api/products/${productId}/tariffs/`).then((response: any) => response.tariffs || []),
  });

  const { data: tariffTypes = [] } = useQuery({
    queryKey: ['tariff-types-active'],
    queryFn: () => api.get('settings/api/tariff-types/active/'),
  });

  const editingRef = useRef(editing);
  useEffect(() => { editingRef.current = editing; }, [editing]);

  useRealtimeEvents((e) => {
    if (e.model === 'warehouses.product' && String(e.pk) === String(productId)) {
      if (!editingRef.current) queryClient.invalidateQueries({ queryKey: ['product', productId] });
    }
    if (e.model === 'warehouses.stockitem') {
      queryClient.invalidateQueries({ queryKey: ['product-stock-items', productId] });
    }
    if (e.model === 'settings.tariff') {
      queryClient.invalidateQueries({ queryKey: ['product-tariffs', productId] });
    }
  });

  // Inicializace stavu formuláře po načtení dat
  useEffect(() => {
    if (product) {
      setForm({
        name: product.name || '',
        sku: product.sku || '',
        description: product.description || '',
        color: product.color || '#066fd1',
        category: product.category ? String(product.category) : '',
        category_option: product.category ? { value: product.category, label: product.category_name || `Kategorie ${product.category}` } : null,
        product_type: product.product_type ? String(product.product_type) : '',
        product_type_option: product.product_type ? { value: product.product_type, label: product.product_type_name || `Typ ${product.product_type}` } : null,
        tracking_type: product.tracking_type || 'SERIALIZED',
        priority: product.priority ?? 0,
        weight: product.weight !== undefined && product.weight !== null ? String(product.weight) : '0',
        weight_unit: product.weight_unit ? String(product.weight_unit) : '',
        weight_unit_option: product.weight_unit ? { value: product.weight_unit, label: product.weight_unit_name || product.weight_unit_code || `Jednotka ${product.weight_unit}` } : null,
        default_daily_rate: product.default_daily_rate ?? product.default_daily_rate_display ?? '',
        price_purchase: product.price_purchase !== undefined && product.price_purchase !== null ? String(product.price_purchase) : '',
        price_purchase_vat: product.price_purchase_vat !== undefined && product.price_purchase_vat !== null ? String(product.price_purchase_vat) : '',
        currency: product.currency ? String(product.currency) : '',
        currency_option: product.currency ? { value: product.currency, label: product.currency_code || product.currency_name || `Měna ${product.currency}` } : null,
        exclude_from_discount: Boolean(product.exclude_from_discount),
        ...productDimensionsForm(product),
      });
    }
  }, [product]);

  // Sazba DPH použitá jen pro TariffFormModal (ProductInfoCard si ji počítá sama z defaultTax)
  const taxRatePercent = defaultTax?.rate ? parseFloat(defaultTax.rate) : 0;

  // Uložení úprav produktu
  const save = useMutation({
    mutationFn: () => {
      const payload: Record<string, any> = {
        name: form.name,
        sku: form.sku,
        description: form.description,
        color: form.color,
        tracking_type: form.tracking_type,
        priority: Number(form.priority) || 0,
        weight: parseFloat(form.weight) || 0,
        default_daily_rate: parseFloat(form.default_daily_rate) || 0,
        exclude_from_discount: form.exclude_from_discount,
        category: form.category ? Number(form.category) : null,
        product_type: form.product_type ? Number(form.product_type) : null,
        weight_unit: form.weight_unit ? Number(form.weight_unit) : null,
        currency: form.currency ? Number(form.currency) : null,
        price_purchase: form.price_purchase !== '' ? parseFloat(form.price_purchase) : null,
        price_purchase_vat: form.price_purchase_vat !== '' ? parseFloat(form.price_purchase_vat) : null,
        ...buildProductDimensionsPayload(form),
      };
      return api.patch(`warehouses/api/products/${productId}/`, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product', productId] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      setEditing(false);
      setError(null);
    },
    onError: (requestError: any) => {
      const data = requestError?.response?.data;
      if (data && typeof data === 'object') {
        const firstErrorKey = Object.keys(data)[0];
        const firstErrorVal = data[firstErrorKey];
        const errorMsg = Array.isArray(firstErrorVal) ? firstErrorVal[0] : firstErrorVal;
        setError(`${firstErrorKey}: ${errorMsg}`);
      } else {
        setError(t('warehouses.products.saveError'));
      }
    },
  });

  const remove = useMutation({
    mutationFn: () => api.delete(`warehouses/api/products/${productId}/`),
    onSuccess: () => navigate('/warehouses/products'),
    onError: (requestError: any) => setError(requestError?.response?.data?.detail || t('warehouses.products.saveError')),
  });

  const handleDelete = async () => {
    if (await confirm(t('warehouses.products.deleteConfirm', { name: product.name }))) remove.mutate();
  };

  const tariffsKey = ['product-tariffs', productId];
  const saveTariff = useMutation({
    mutationFn: () => {
      const payload = {
        name: tariffForm.name,
        tariff_type: tariffForm.tariff_type,
        amount: parseFloat(tariffForm.amount),
        amount_vat: tariffForm.amount_vat ? parseFloat(tariffForm.amount_vat) : null,
        valid_from: tariffForm.valid_from || null,
        valid_to: tariffForm.valid_to || null,
        priority: parseInt(String(tariffForm.priority), 10) || 0,
        is_active: tariffForm.is_active,
        is_promotional: tariffForm.is_promotional,
        note: tariffForm.note || '',
        description: tariffForm.description || '',
      };
      return editingTariff
        ? api.put(`warehouses/api/products/${productId}/rental-tariffs/${editingTariff.id}/`, payload)
        : api.post(`warehouses/api/products/${productId}/create_rental_tariff/`, payload);
    },
    onSuccess: () => {
      handleCloseTariffModal();
      queryClient.invalidateQueries({ queryKey: tariffsKey });
    },
    onError: (requestError: any) => setTariffError(requestError?.response?.data?.detail || 'Tarif se nepodařilo uložit.'),
  });

  const deleteTariff = useMutation({
    mutationFn: (tariffId: number) => api.delete(`warehouses/api/products/${productId}/rental-tariffs/${tariffId}/`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: tariffsKey }),
    onError: (requestError: any) => setTariffError(requestError?.response?.data?.detail || 'Tarif se nepodařilo smazat.'),
  });

  const handleOpenTariffModal = (tariff: any = null) => {
    setTariffError(null);
    if (tariff) {
      setEditingTariff(tariff);
      const tariffTypeId = typeof tariff.tariff_type === 'object' && tariff.tariff_type !== null ? tariff.tariff_type.id : tariff.tariff_type;
      setTariffForm({
        name: tariff.name || '',
        tariff_type: tariffTypeId || null,
        amount: tariff.amount,
        amount_vat: tariff.amount_vat || '',
        valid_from: tariff.valid_from || '',
        valid_to: tariff.valid_to || '',
        priority: tariff.priority,
        is_active: tariff.is_active,
        is_promotional: tariff.is_promotional,
        note: tariff.note || '',
        description: tariff.description || '',
      });
    } else {
      setEditingTariff(null);
      setTariffForm(emptyTariffForm);
    }
    setShowTariffModal(true);
  };

  const handleCloseTariffModal = () => {
    setShowTariffModal(false);
    setEditingTariff(null);
    setTariffForm(emptyTariffForm);
  };

  const handleTariffFieldChange = (field: string, value: any) => setTariffForm((prev) => ({ ...prev, [field]: value }));
  const handleTariffSubmit = (event: React.FormEvent) => { event.preventDefault(); setTariffError(null); saveTariff.mutate(); };
  const handleDeleteTariff = async (tariffId: number) => {
    if (!await confirm(t('warehouses.tariffs.confirmDelete'))) return;
    deleteTariff.mutate(tariffId);
  };

  const stockItemsKey = ['product-stock-items', productId];
  const saveStockItem = useMutation({
    mutationFn: () => api.patch(`warehouses/api/stock-items/${editingStockItem.id}/`, {
      quantity_owned: Number(stockForm.quantity_owned) || 0,
      quantity_available: Number(stockForm.quantity_available) || 0,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: stockItemsKey });
      handleCloseStockModal();
    },
    onError: (requestError: any) => {
      const data = requestError?.response?.data;
      setStockError(data?.quantity_available?.[0] || data?.non_field_errors?.[0] || data?.detail || t('warehouses.products.stockSaveError'));
    },
  });

  const handleOpenStockModal = (stockItem: any) => {
    setStockError(null);
    setEditingStockItem(stockItem);
    setStockForm({ quantity_owned: String(stockItem.quantity_owned), quantity_available: String(stockItem.quantity_available) });
    setShowStockModal(true);
  };

  const handleCloseStockModal = () => {
    setShowStockModal(false);
    setEditingStockItem(null);
    setStockError(null);
  };

  const handleStockSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (Number(stockForm.quantity_available) > Number(stockForm.quantity_owned)) {
      setStockError(t('warehouses.products.stockAvailableExceedsOwned'));
      return;
    }
    setStockError(null);
    saveStockItem.mutate();
  };

  const formatDate = (dateString: string) => (dateString ? new Date(dateString).toLocaleDateString() : '-');
  const isValidityActive = (validFrom: string, validTo: string) => {
    const now = new Date();
    const from = validFrom ? new Date(validFrom) : null;
    const to = validTo ? new Date(validTo) : null;
    if (from && to) return now >= from && now <= to;
    if (from) return now >= from;
    if (to) return now <= to;
    return true;
  };

  if (isLoading) return <PageShell pretitle={t('warehouses.products.catalog')} title={t('warehouses.products.detail')}><CenteredLoader /></PageShell>;
  if (isError || !product || !form) return <PageShell pretitle={t('warehouses.products.catalog')} title={t('warehouses.products.detail')}><PageSection><Alert variant="danger" className="mb-0">{t('warehouses.products.notFound')}</Alert></PageSection></PageShell>;

  return (
    <PageShell
      pretitle={t('warehouses.products.catalog')}
      title={product.name}
      description={`${product.sku} ${product.category_name ? `· ${product.category_name}` : ''}`}
      actions={
        <div className="d-flex gap-2">
          <Button as={Link as any} to="/warehouses/products" variant="outline-secondary">
            <IconArrowLeft size={16} className="me-1" />
            {t('warehouses.products.catalog')}
          </Button>
          {!editing && (
            <Button variant="outline-primary" onClick={() => setEditing(true)}>
              <IconEdit size={16} className="me-1" />
              {t('warehouses.products.edit')}
            </Button>
          )}
          <Button variant="outline-danger" onClick={handleDelete} disabled={remove.isPending}>
            <IconTrash size={16} className="me-1" />
            {t('warehouses.products.delete')}
          </Button>
        </div>
      }
    >
      {error && (
        <PageSection>
          <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-0">
            {error}
          </Alert>
        </PageSection>
      )}

      <PageSection title={t('warehouses.products.productCard')}>
        <ProductInfoCard
          product={product}
          form={form}
          setForm={setForm}
          editing={editing}
          defaultTax={defaultTax}
          saving={save.isPending}
          onSubmit={() => save.mutate()}
          onCancel={() => setEditing(false)}
        />
      </PageSection>

      <PageSection title={t('warehouses.products.stockByWarehouse')} bodyClassName="p-0">
        <ProductStockTable
          stockItems={stockItems}
          trackingType={product.tracking_type}
          onEditStock={handleOpenStockModal}
        />
      </PageSection>

      <PageSection
        title={t('warehouses.products.tariffs')}
        description={t('warehouses.products.tariffsDescription')}
        bodyClassName="p-0"
        actions={
          canManage && (
            <Button size="sm" onClick={() => handleOpenTariffModal()}>
              <IconPlus size={16} className="me-1" />
              {t('warehouses.products.addTariff')}
            </Button>
          )
        }
      >
        {tariffError && (
          <Alert variant="danger" dismissible onClose={() => setTariffError(null)} className="app-inline-alert m-3 mb-0">
            {tariffError}
          </Alert>
        )}
        <TariffListTable
          tariffs={tariffs}
          canManage={canManage}
          onEdit={handleOpenTariffModal}
          onDelete={handleDeleteTariff}
          onCreate={() => handleOpenTariffModal()}
          formatCurrency={formatCurrency}
          formatDate={formatDate}
          isValidityActive={isValidityActive}
          t={t}
        />
      </PageSection>

      <TariffFormModal
        show={showTariffModal}
        editing={Boolean(editingTariff)}
        formData={tariffForm}
        tariffTypes={tariffTypes}
        submitting={saveTariff.isPending}
        vatRate={taxRatePercent}
        selectTheme={selectTheme}
        onClose={handleCloseTariffModal}
        onSubmit={handleTariffSubmit}
        onChange={handleTariffFieldChange}
      />

      <ProductStockEditModal
        show={showStockModal}
        stockName={editingStockItem?.stock_name}
        formData={stockForm}
        error={stockError}
        submitting={saveStockItem.isPending}
        onClose={handleCloseStockModal}
        onSubmit={handleStockSubmit}
        onChange={(field, value) => setStockForm((prev) => ({ ...prev, [field]: value }))}
      />
    </PageShell>
  );
}
